import { Controller } from "stimulus";

export default class extends Controller {
  static targets = ['horizontalFreeze', 'verticalFreeze', 'agenda', 'agendaContainer', 'schedules'];

  connect() {
    this.element[this.identifier] = this;
    this.updateFrequencyMinutes = 1;

    this.applyMask();
    this.mirrorAgendaScrolling();
    this.initializeCards();
    this.isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    this.isCurrentWeek() ? this.setCurrentTime() : this.scrollToCurrentTime();

    if (window.matchMedia("(max-width: 576px)").matches) {
      this.bindingAgendaMobileScrolling();
      this.addingBulletAtNotEmptyColumns();
    }
  }

  isCurrentWeek() {
    let [year, month, day] = this.element.dataset.date.split('-'),
      calendarDate = new Date(+year, +month - 1, +day),
      beginningOfWeek = this.getSunday(calendarDate),
      endingOfWeek = this.getSatuday(calendarDate),
      currentDate = new Date();
    return currentDate >= beginningOfWeek && currentDate <= endingOfWeek;
  }

  getSunday(d) {
    d = new Date(d);
    let day = d.getDay(),
        diff = d.getDate() - day;
    return new Date(d.setDate(diff));
  }

  getSatuday(d) {
    let date = this.getSunday(d);
    date.setDate(date.getDate() + 6);
    return date;
  }

  bindingAgendaMobileScrolling() {
    let headerCells = this.horizontalFreezeTarget.querySelectorAll('.header-container .table-cell'),
      $agenda = this.agendaTarget;
    headerCells.forEach(($cell) => {
      $cell.addEventListener('click', (ev) => {
        headerCells.forEach(($el) => $el.classList.toggle('selected', false));
        $cell.classList.toggle('selected', true);
        let $tableCell = $agenda.querySelector(`.table-row .table-cell:nth-child(${$cell.dataset.index})`)
        $agenda.scrollTo({ left: $tableCell.offsetLeft, behavior: 'auto' });
      });
    });
  }

  addingBulletAtNotEmptyColumns() {
    this.horizontalFreezeTarget.querySelectorAll('.header-container .table-cell').forEach(($el) => {
      if (this.agendaTarget.querySelectorAll(`.table-row .table-cell[data-day-index="${+$el.dataset.index - 1}"] .card`).length > 0) {
        $el.classList.add('contain-schedules');
      }
    });
  }

  clearHeaderSelection() {
    this.horizontalFreezeTarget.querySelectorAll('.header-container .table-cell').forEach(($el) => $el.classList.toggle('selected', false));
  }

  mirrorAgendaScrolling() {
    $(this.agendaTarget).on('scroll', (ev) => {
      if (this.isIOS)
        this.agendaTarget.scrollLeft = this.agendaTarget.querySelector(`.table-row .table-cell:nth-child(${this.horizontalFreezeTarget.querySelector('.header-container .table-cell.selected').dataset.index})`).offsetLeft - 2;
      this.verticalFreezeTarget.scrollTop = this.agendaTarget.scrollTop;
      this.horizontalFreezeTarget.scrollLeft = this.agendaTarget.scrollLeft;
    });
  }

  initializeCards() {
    this.adjustTableCellCards(this.agendaContainerTarget.querySelectorAll('.table-row .table-cell'));
    this.agendaContainerTarget.querySelectorAll('.table-cell-container:not(.disabled) .card:not(.readonly)').forEach(($card) => this.bindCardInteractions($card));
  }

  bindCardInteractions($card) {
    if ($card.dataset.initialized === 'true') return;
    $card.dataset.initialized = 'true';
    $card.addEventListener('click', () => this.prepareToOpenCard($card));
    $card.addEventListener('mouseenter', () => this.scheduleExpandOnHover($card));
    $card.addEventListener('mouseleave', () => this.cancelOrCollapseHover($card));
  }

  // Hover-intent: only expand once the pointer rests on the card for a short delay, so sweeping the
  // mouse across many stacked cards no longer fires a burst of expand/collapse animations.
  scheduleExpandOnHover($card) {
    this.cancelPendingHover();
    this._pendingHoverCard = $card;
    this._hoverTimer = setTimeout(() => {
      this._hoverTimer = null;
      this._pendingHoverCard = null;
      $card._hoverExpanded = true;
      this.expandCardOnHover($card);
    }, this.hoverStartDelayMs);
  }

  cancelOrCollapseHover($card) {
    // Left before the intent delay elapsed: nothing was expanded, just cancel the pending timer.
    if (this._pendingHoverCard === $card) return this.cancelPendingHover();

    if (!$card._hoverExpanded) return;
    $card._hoverExpanded = false;
    this.collapseCardOnHover($card);
  }

  cancelPendingHover() {
    if (this._hoverTimer) clearTimeout(this._hoverTimer);
    this._hoverTimer = null;
    this._pendingHoverCard = null;
  }

  disconnect() {
    this.cancelPendingHover();
  }

  setCurrentTime() {
    this.agendaContainerTarget.insertAdjacentHTML('afterBegin', '<div class="current-time-mark"></div>');
    this.currentTimeMark = this.agendaContainerTarget.querySelector('.current-time-mark');
    window.addEventListener('resize', () => this.updateCurrentTimeMark());
    this.agendaTarget.scrollTo(this.updateCurrentTimeMark());
    this.agendaTarget.style.scrollBehavior = (this.isIOS ? 'auto' : 'smooth');
    setTimeout(() => {
      this.intervalUpdateMarkTime = setInterval(() => this.updateCurrentTimeMark(), this.updateFrequencyMinutes * 60 * 1000);
    }, (59 - new Date().getSeconds()) * 1000);
  }

  scrollToCurrentTime() {
    this.agendaTarget.scrollTo(this.rearrangeTileToScroll(this.findCalendarTile()));
  }

  updateCurrentTimeMark() {
    let now = new Date(),
      tile = this.findTodayTile(now);

    if (!tile.column || !tile.row) return this.rearrangeTileToScroll(this.findCalendarTile());

    let offsetTop = ((tile.column.clientHeight * this.updateFrequencyMinutes) / 60) * Math.floor(now.getMinutes() / this.updateFrequencyMinutes);

    this.currentTimeMark.style.top = `${tile.row.offsetTop + offsetTop}px`;
    this.currentTimeMark.style.left = `${tile.column.offsetLeft}px`;
    this.currentTimeMark.style.width = `${tile.column.clientWidth - 2}px`;

    return this.rearrangeTileToScroll(tile);
  }

  findCalendarTile() {
    let currentRow = this.agendaContainerTarget.querySelector(`.table-row[data-time="${new Date().getHours()}"]`),
      currentColumn = currentRow.querySelector(`.table-cell[data-day-index="${this.currentCalendarDate.getDay()}"]`);

    return { column: currentColumn, row: currentRow };
  }

  findTodayTile(now = new Date()) {
    let currentRow = this.agendaContainerTarget.querySelector(`.table-row[data-time="${now.getHours()}"]`);
    if (!currentRow) return { column: null, row: null };

    let currentColumn = currentRow.querySelector(`.table-cell[data-day-index="${now.getDay()}"]`);
    return { column: currentColumn, row: currentRow };
  }

  rearrangeTileToScroll(tile) {
    return { left: tile.column.offsetLeft - 2, top: tile.row.offsetTop - 100 };
  }

  adjustTableCellCards($tableCells) {
    $tableCells.forEach(($tableCell) => {
      const observer = new MutationObserver(() => this.adjustCell($tableCell));
      observer.observe($tableCell, { subtree: true, childList: true });
      this.adjustCell($tableCell);
    });
  }

  adjustCell($tableCell) {
    let $cards = $tableCell.querySelectorAll('.card'),
        cardsSize = $cards.length,
        ratio = 1/cardsSize;

    $cards.forEach(($card) => {
      let $tableCellContainer = $card.closest('.table-cell-container');

      if (!$tableCellContainer)
        return;

      !$card.classList.contains('readonly') && !$tableCellContainer.classList.contains('disabled') && this.bindCardInteractions($card);

      $card.style.width = $card.classList.contains('draft') || $card.classList.contains('open') ? `${$tableCellContainer.offsetWidth - 15}px` : `calc(${$tableCellContainer.offsetWidth - 15}px * ${ratio})`;
      !$card.classList.contains('open') && ($card.style.position = 'relative');
      $card.style.removeProperty('left');
      $card.style.removeProperty('top');
      $card.style.removeProperty('margin-left');
      $card.classList.remove('stacked');
      this.applyTimeBasedSizing($card, $tableCell);
    });

    this.overlapCellCards($cards);

    // Snapshot the canonical resting layout of every card. Hover expand/collapse always restores
    // to THIS state, so no matter how transitions overlap the card lands on the exact same home.
    $cards.forEach(($card) => this.captureRestingState($card));
  }

  overlapCellCards($cards) {
    let stackable = Array.from($cards).filter(($card) => !$card.classList.contains('open') && !$card.classList.contains('draft'));
    if (stackable.length < 2) return;

    let $tableCellContainer = stackable[0].closest('.table-cell-container');
    if (!$tableCellContainer) return;

    let containerWidth = $tableCellContainer.offsetWidth - 15,
        n = stackable.length,
        stepX = Math.max(22, Math.min(45, Math.floor((containerWidth - 55) / Math.max(1, n - 1)))),
        cardWidth = Math.max(55, containerWidth - (n - 1) * stepX);

    stackable.forEach(($card, idx) => {
      $card.style.width = `${cardWidth}px`;
      let baseZ = parseInt($card.style.zIndex || '0', 10);
      $card.style.zIndex = `${baseZ + idx}`;
      if (idx === 0) return;

      $card.style.marginLeft = `${-cardWidth + stepX}px`;
      $card.classList.add('stacked');
    });
  }

  applyTimeBasedSizing($card, $tableCell) {
    $card.style.removeProperty('margin-top');
    $card.style.removeProperty('height');
    if (!$card.classList.contains('open'))
      $card.style.removeProperty('z-index');

    if ($card.classList.contains('open') || $card.classList.contains('draft'))
      return;

    let schedule;
    try { schedule = JSON.parse($card.dataset.schedule || '{}'); } catch (e) { return; }

    let startTime = schedule.start_time || schedule.startTime,
        endTime = schedule.end_time || schedule.endTime;
    if (!startTime || !endTime) return;

    let [sh, sm] = startTime.split(':').map((n) => parseInt(n, 10)),
        [eh, em] = endTime.split(':').map((n) => parseInt(n, 10));
    if ([sh, sm, eh, em].some(Number.isNaN)) return;

    let durationMinutes = (eh - sh) * 60 + (em - sm);
    if (durationMinutes <= 0) return;

    let cellHeight = $tableCell.offsetHeight;
    if (!cellHeight) return;

    let halfCells = Math.ceil(durationMinutes / 30);

    $card.style.marginTop = `${sm >= 30 ? cellHeight / 2 : 0}px`;
    $card.style.height = `${halfCells * (cellHeight / 2)}px`;
    $card.style.zIndex = `${sh * 100}`;
  }

  // Records a card's canonical resting layout (the stacked "home" position/size). Every hover
  // interaction restores to this exact state, which is what keeps overlapping animations correct.
  captureRestingState($card) {
    if ($card.classList.contains('open')) return;

    $card._rest = {
      position: $card.style.position,
      left: $card.style.left,
      top: $card.style.top,
      marginLeft: $card.style.marginLeft,
      marginTop: $card.style.marginTop,
      width: $card.style.width,
      height: $card.style.height,
      zIndex: $card.style.zIndex
    };
  }

  // Puts a card back exactly at its canonical resting layout. Idempotent: calling it any number of
  // times, in any order, always leaves the card in the same home position/size.
  applyRestingState($card) {
    $card.style.transition = this.restTransition;

    let rest = $card._rest;
    if (!rest) return;

    $card.style.position = rest.position;
    $card.style.left = rest.left;
    $card.style.top = rest.top;
    $card.style.marginLeft = rest.marginLeft;
    $card.style.marginTop = rest.marginTop;
    $card.style.width = rest.width;
    $card.style.height = rest.height;
    $card.style.zIndex = rest.zIndex;
  }

  expandCardOnHover($card) {
    if ($card.classList.contains('open')) return;

    let $tableCellContainer = $card.closest('.table-cell-container');
    if (!$tableCellContainer) return;

    let $cards = [...$tableCellContainer.querySelectorAll('.card')].filter(($c) => !$c.classList.contains('open'));
    if (!$cards.includes($card)) return;

    // Settle any in-flight hover first: bump each card's generation (cancels pending restores) and
    // force it back to its canonical resting layout, so the measurements below are the true home
    // positions and never a transient mid-animation state.
    $cards.forEach(($c) => {
      if (!$c._rest) this.captureRestingState($c);
      $c._hoverGen = ($c._hoverGen || 0) + 1;
      this.applyRestingState($c);
    });

    void $tableCellContainer.offsetHeight;

    // Pin every card at its (canonical) resting position/size so the hovered card can transition
    // from a concrete start state — a CSS transition from `auto` would not run.
    let snapshots = $cards.map(($c) => ({ el: $c, offsetLeft: $c.offsetLeft, offsetTop: $c.offsetTop, width: `${$c.offsetWidth}px` }));
    snapshots.forEach((snap) => {
      snap.el.style.position = 'absolute';
      snap.el.style.left = `${snap.offsetLeft}px`;
      snap.el.style.top = `${snap.offsetTop}px`;
      snap.el.style.marginLeft = '0';
      snap.el.style.marginTop = '0';
      snap.el.style.width = snap.width;
    });

    let hoveredSnap = snapshots.find((snap) => snap.el === $card);
    if (!hoveredSnap) return;
    // The exact geometry to animate back to on collapse (canonical resting, in px).
    $card._pin = { offsetLeft: hoveredSnap.offsetLeft, width: hoveredSnap.width };

    void $tableCellContainer.offsetHeight;

    // Expand the hovered card across the full time-slot width so its content is readable regardless
    // of its position: the leftmost grows to the right edge, the rightmost grows back to the left.
    let leftEdge = Math.min(...snapshots.map((snap) => snap.offsetLeft)),
        cellContentRight = $tableCellContainer.clientWidth - 10;

    $card.style.transition = this.hoverTransition;
    $card.style.left = `${leftEdge}px`;
    $card.style.width = `${cellContentRight - leftEdge}px`;
    $card.style.zIndex = '5000';
  }

  collapseCardOnHover($card) {
    if ($card.classList.contains('open')) return;

    let $tableCellContainer = $card.closest('.table-cell-container');
    if (!$tableCellContainer) return;

    [...$tableCellContainer.querySelectorAll('.card')].forEach(($c) => {
      if ($c.classList.contains('open')) return;

      // Non-hovered cards never moved from home (or we have no pin to animate) — restore now.
      if ($c !== $card || !$c._pin) {
        $c._hoverGen = ($c._hoverGen || 0) + 1;
        this.applyRestingState($c);
        return;
      }

      let pin = $c._pin,
          gen = ($c._hoverGen = ($c._hoverGen || 0) + 1);

      // Animate the hovered card back to its resting geometry, then snap to the relative home
      // layout once settled. Guarded by the generation token so a stale finish (or a slow timeout
      // firing after a newer hover took over) can never restore the wrong card out of position.
      $c.style.transition = this.hoverTransition;
      $c.style.left = `${pin.offsetLeft}px`;
      $c.style.width = pin.width;
      $c.style.removeProperty('z-index');

      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        $c.removeEventListener('transitionend', finish);
        if ($c._hoverGen !== gen) return;
        this.applyRestingState($c);
      };
      $c.addEventListener('transitionend', finish);
      setTimeout(finish, this.hoverAnimationMs + 80);
    });
  }

  prepareToOpenCard($card) {
    // A click opens the card: drop any pending hover-intent expand so it can't fire after opening.
    this.cancelPendingHover();
    $card._hoverExpanded = false;

    let $tableCellContainer = $card.closest('.table-cell-container'),
        cardRect = $card.getBoundingClientRect(),
        containerRect = $tableCellContainer.getBoundingClientRect(),
        cardLeftWithin = cardRect.left - containerRect.left,
        cardTopWithin = cardRect.top - containerRect.top;

    $card.classList.add('open');
    $card.offsetParent && $card.offsetParent.classList.add('opened-card');
    $card.style.removeProperty('margin-top');
    $card.style.removeProperty('margin-left');
    $card.style.position = 'absolute';
    $card.style.top = `${cardTopWithin}px`;
    $card.style.left = `${cardLeftWithin}px`;
    $card.style.width = `${$tableCellContainer.clientWidth - 10 - cardLeftWithin}px`;
    $card.style.zIndex = this.zIndex + 10000;
    let scheduleMask = document.querySelector('.schedule-mask');
    scheduleMask.style.zIndex = this.zIndex + 9000;
    scheduleMask.classList.add('opened');
  }

  slideCard($card, $tableCellContainer) {
    let cardStyleLeft = +$card.style.left.replace(/\D/gi, '');

    if (cardStyleLeft >= $tableCellContainer.offsetLeft) {
      $card.style.left = `${cardStyleLeft - 3}px`;
      setTimeout(this.slideCard.bind(this, $card, $tableCellContainer), 0.5);
    } else {
      $card.style.width = `calc(${$tableCellContainer.clientWidth}px - 10px)`;
    }
  }

  closeCard() {
    let $card = this.element.querySelector('.card.open');

    if (!$card)
      return;

    $card.classList.remove('open');
    $card.style.removeProperty('z-index');
    !!$card.offsetParent && $card.offsetParent.classList.remove('opened-card');
    let scheduleMask = document.querySelector('.schedule-mask');
    scheduleMask.classList.remove('opened');
    scheduleMask.style.removeProperty('z-index');
    this.adjustTableCellCards($card.closest('.table-row').querySelectorAll('.table-cell'));
  }

  applyMask() {
    document.querySelectorAll('.schedule-mask').forEach(($el) => $el.remove());
    document.querySelector('body').insertAdjacentHTML('beforeEnd', '<div class="schedule-mask"></div>');
    document.querySelector('.schedule-mask').addEventListener('click', () => this.closeCard());
  }

  get hoverStartDelayMs() {
    return 400;
  }

  get hoverAnimationMs() {
    return 160;
  }

  get hoverTransition() {
    return `left ${this.hoverAnimationMs}ms ease, width ${this.hoverAnimationMs}ms ease`;
  }

  // Resting transition: keeps the default opacity animation but excludes left/width, so pinning
  // and home restores never animate — only the explicit hover expand/collapse does.
  get restTransition() {
    return 'opacity 80ms linear';
  }

  get zIndex() {
    let closestModal = this.element.closest('.modal');
    if (!closestModal) return 5;

    return +getComputedStyle(closestModal).zIndex + 10;
  }

  get currentCalendarDate() {
    let [year, month, day] = this.element.dataset.date.split('-');
    return new Date(+year, +month - 1, +day);
  }
};